<template>
    <div>
        <!-- Completion ceremony: shown when finishing the final lesson completed
             the course (the only system-initiated completion the student watches) -->
        <div v-if="ceremony" class="content-frame content ceremony has-text-centered">
            <div class="ceremony-inner">
                <i class="fad fa-circle-check ceremony-check has-text-success mb-5"></i>
                <h1 class="is-size-2 has-text-weight-bold mb-4">Course Complete!</h1>
                <p class="is-size-5 mb-5">Congratulations &mdash; you've finished all the requirements for <strong>{{ enrollment.course.title }}</strong>.</p>
                <p class="is-size-6 has-text-grey mb-6">Your course responses are now locked, and your certificates and completion forms are unlocked.</p>
                <div class="ceremony-actions">
                    <router-link :to="{name: 'course-applications', params: {enrollmentId: enrollmentId}}">
                        <b-button type="is-success" size="is-large" class="mb-3" expanded><i class="fad fa-file-certificate mr-3"></i>View Certifications &amp; Forms</b-button>
                    </router-link>
                    <router-link :to="{name: 'course-welcome', params: {enrollmentId: enrollmentId}}">
                        <b-button type="is-light" expanded>Back to Course Information</b-button>
                    </router-link>
                </div>
            </div>
        </div>
        <!-- Finish-or-wait choice: coursework is done and only OPTIONAL
             applications remain, so the course does NOT complete on its own —
             completing would lock study time and could forfeit an optional
             application's recorded-time minimum (e.g. WA clock hours). The
             student decides. -->
        <div v-else-if="readyToComplete" class="content-frame content ceremony has-text-centered">
            <div class="ceremony-inner">
                <i class="fad fa-flag-checkered ceremony-check has-text-info mb-5"></i>
                <h1 class="is-size-2 has-text-weight-bold mb-4">Coursework Finished!</h1>
                <p class="is-size-5 mb-5">You've finished all the lessons in <strong>{{ enrollment.course.title }}</strong>. You can complete your course now, or keep it open a little longer &mdash; here's why you might wait:</p>
                <div class="has-text-left mb-5">
                    <b-message v-for="app in unmetOptionalApps" :key="'unmet-' + app.course_application_id" type="is-warning">
                        The <strong>{{ app.title }}</strong> application in <strong style="white-space: nowrap;"><i class="fad fa-file-certificate mr-2"></i>Certifications &amp; Forms</strong> requires at least <strong>{{ app.min_minutes_required }}</strong> minutes of recorded study time, and you've recorded <strong>{{ confirmTotalMinutes }}</strong> so far.
                        If you complete your course now, your study time will be locked and you won't be able to apply for it.
                    </b-message>
                    <b-message v-for="app in metOptionalApps" :key="'met-' + app.course_application_id" type="is-info">
                        The <strong>{{ app.title }}</strong> application remains available in <strong style="white-space: nowrap;"><i class="fad fa-file-certificate mr-2"></i>Certifications &amp; Forms</strong> after you complete your course, but your recorded study time will be locked in at <strong>{{ confirmTotalMinutes }}</strong> minute(s).
                    </b-message>
                </div>
                <p class="is-size-6 has-text-grey mb-6">Completing your course locks your responses and study time &mdash; no further time can be recorded. This cannot be undone.</p>
                <div class="ceremony-actions">
                    <b-button type="is-success" size="is-large" class="mb-3" expanded :loading="completing" @click="finishCourse">
                        <i class="fad fa-circle-check mr-3"></i>Complete My Course
                    </b-button>
                    <router-link :to="{name: 'course-welcome', params: {enrollmentId: enrollmentId}}">
                        <b-button type="is-light" expanded>Not Yet &mdash; Keep My Course Open</b-button>
                    </router-link>
                </div>
            </div>
        </div>
        <div v-else-if="!loading && component">
            <b-message type="is-warning" class="time-paused-banner" v-if="milestoneLock.locked">
                <i class="far fa-circle-pause mr-2"></i>
                <strong>Time tracking is paused.</strong>
                You've reached your milestone checkpoint — course time will not be recorded until your reflection has been submitted and approved.
                You're welcome to review the content meanwhile.
                <router-link :to="{name: 'enroll-reflections', params: {enrollmentId: enrollmentId}}"><strong>Go to Reflections</strong></router-link>
            </b-message>
            <b-message type="is-warning" class="time-paused-banner" v-else-if="isAppIdle && !videoPlaying && enrollment.course.show_timer">
                <i class="far fa-circle-pause mr-2"></i>
                <strong>Are you still there?</strong>
                Time tracking is paused due to inactivity. You'll be taken back to the course overview in {{ timeout }}s.
                Move your mouse or press a key to resume.
            </b-message>
            <component
                :is="component.type.toLowerCase() + '-component'"
                :component="component"
                :enrollment="enrollment"
                :outline="outline"
                :time="lessonTime"
                :milestone-lock="milestoneLock"
                @saveData="saveData"
                @goto="goToOutline"
                @complete="completeComponent"
            />
        </div>
        <div class="content-frame content pl-5 pt-6" v-else>
            <h4 class="has-text-grey"><i class="fas fa-spinner fa-spin mr-4"></i>Please wait. Loading content...</h4>
        </div>
    </div>
</template>

<script>
    import {v4 as uuidv4} from 'uuid';
    import moment from "moment";
    import async from "async";

    import {client as http} from "@/http_client";
    import RenderEditorJsComponent from "../../components/RenderComponent";


    import LessonComponent from "../../components/course/LessonComponent";
    import QuizComponent from "../../components/course/QuizComponent";
    import SurveyComponent from "../../components/course/SurveyComponent";
    import CheckpointComponent from "../../components/course/CheckpointComponent";
    import {mapGetters, mapActions} from "vuex";

    export default {
        components: {LessonComponent, QuizComponent, CheckpointComponent, SurveyComponent},
        props: ['outlineId'],
        data: function() {
            return {
                loading: true,
                component: {
                    content: '{}'
                },
                outline: {},
                prevOutlineId: null,
                nextOutlineId: null,
                timer: null,
                time: 0,
                lockTick: 0,
                uuid: null,
                timeout: 60,
                ceremony: false,
                readyToComplete: false,
                completionConfirm: null,
                completing: false
            };
        },
        computed: {
            ...mapGetters(['enrollment', 'milestoneLock', 'videoPlaying']),
            // Get enrollmentId from route params (not passed as prop from nested route)
            enrollmentId: function() {
                return this.$route.params.enrollmentId;
            },
            currentItemId: function() {
                return this.outlineId;
            },
            formattedTime: function() {
                return moment.utc(this.time * 1000).format('HH:mm:ss');
            },
            formattedMinTime: function() {
                return moment.utc(this.outline.require_min_time * 60 * 1000).format('HH:mm:ss');
            },
            formattedLessonTime: function() {
                return moment.utc((this.time * 1000) + (this.outline.time * 60 * 1000)).format('HH:mm:ss');
            },
            lessonTime: function() {
                return this.time + (this.outline.time * 60);
            },
            confirmTotalMinutes: function() {
                return (this.completionConfirm && this.completionConfirm.total_minutes) || 0;
            },
            unmetOptionalApps: function() {
                if (!this.completionConfirm || !this.completionConfirm.optional_apps) return [];
                return this.completionConfirm.optional_apps.filter(a => a.min_minutes_required && !a.min_minutes_met);
            },
            metOptionalApps: function() {
                if (!this.completionConfirm || !this.completionConfirm.optional_apps) return [];
                return this.completionConfirm.optional_apps.filter(a => !a.min_minutes_required || a.min_minutes_met);
            }
        },
        methods: {
            loadByOutline: function() {
                if (this.timer) clearInterval(this.timer);
                this.$store.commit('VIDEO_RESET'); // clear any stale playing count from the previous lesson
                this.time = 0;
                this.uuid = uuidv4();
                this.loading = true;
                this.timeout = 60;
                this.outline = {};

                async.series([
                    (cb) => {
                        let prevOutlineId;
                        let count = 0;

                        sectionLoop:
                            for (let i = 0; i < this.enrollment.course.outline.length; i++) {
                                let section = this.enrollment.course.outline[i];

                                for (let j = 0; j < section.components.length; j++) {
                                    count++;
                                    const component = section.components[j];

                                    if (component.component_id === parseInt(this.currentItemId)) {
                                        this.outline = {...component, prevOutlineId, nextOutlineId: null, nextSectionId: null};
                                        this.outline.counts = {
                                            ofSection: (j + 1),
                                            totalSection: section.components.length,
                                            ofTotal: count
                                        };
                                        this.outline.section = section;
                                        this.outline.nextOutlineId = (section.components[j + 1]) ? section.components[j + 1].component_id : null;

                                        if (!this.outline.nextOutlineId) { // lets look to the next section still
                                            if (this.enrollment.course.outline[i + 1]) {
                                                if (this.enrollment.course.outline[i + 1].description) {
                                                    this.outline.nextSectionId = this.enrollment.course.outline[i + 1].type_id;
                                                }
                                                this.outline.nextOutlineId = (this.enrollment.course.outline[i + 1].components[0]) ? this.enrollment.course.outline[i + 1].components[0].component_id : null;
                                            }
                                        }

                                        break sectionLoop;
                                    } else {
                                        prevOutlineId = component.component_id;
                                    }
                                }
                            }

                        // min time injected into the lesson content
                        if (this.outline.content) {
                            let content = JSON.parse(this.outline.content, this.outline.require_min_time);
                            if (content && content.attributes && content.attributes.minTimeToggle && parseInt(content.attributes.minTime)) {
                                this.outline.require_min_time = parseInt(content.attributes.minTime);
                            }
                        }
                        //

                        cb();

                    },
                    (cb) => {
                        http.get('app/components/' + this.outline.component_id).then(response => {
                            this.component = response.data;
                            cb();
                        }).catch(err => {
                            console.log(err);
                            cb(err);
                        });
                    }
                ], (err) => {
                    if (err) console.log(err);

                    this.startTimer();
                    this.sendPing();

                    this.loading = false;
                });
            },

            ...mapActions(['getMilestoneLock', 'getEnrollmentById']),
            saveData: function(payload) {
                payload.enrollment_id = parseInt(this.enrollmentId);
                http.post('/app/enrollments/save', payload, {force: true}).catch(err => {
                    window.alert('There was an issue saving your data. If this problem persists please contact us.');
                });
            },

            startTimer: function() {
                // "Show Lesson Timer" off = no automatic time tracking: no
                // accrual, no recurring pings, no idle auto-timeout. The single
                // on-load ping still runs for progress/position tracking (the
                // API skips the study_log write for these courses too).
                if (!this.enrollment.course.show_timer) {
                    console.log('Timer: disabled for this course (show_timer off)');
                    return;
                }
                console.log('Timer: starting');
                this.timer = setInterval(() => {
                    // While milestone-locked the timer freezes and we only poll for the unlock
                    if (this.milestoneLock.locked) {
                        this.lockTick++;
                        if (this.lockTick % 60 === 0) this.getMilestoneLock(this.enrollment.id);
                        return;
                    }
                    // Idle auto-timeout: once the app has been idle (no interaction for
                    // idleTime, see main.js), stop accruing time and stop pinging so an
                    // unattended window can't collect study time. After a short grace
                    // period, leave the lesson entirely. A playing lesson video counts
                    // as activity (passive watching is study time), so it suppresses this.
                    if (this.isAppIdle && !this.videoPlaying) {
                        this.timeout--;
                        if (this.timeout <= 0) {
                            console.log('Timer: idle timeout, leaving lesson');
                            clearInterval(this.timer);
                            // Exit just the lesson viewer, back to the course information screen
                            this.$router.push({name: 'course', params: {enrollmentId: this.enrollmentId}});
                        }
                        return;
                    }
                    this.time++;
                    if (this.time % 60 === 0) this.sendPing();
                }, 1000);
            },

            sendPing: function() {
                let payload = {
                    session_id: this.uuid,
                    time: this.time,
                    component_id: this.component.id,
                    course_id: this.enrollment.course_id,
                    enrollment_id: this.enrollment.id,
                    type: 'course_outline',
                    type_id: this.outline.id
                };

                http.post('/app/enrollments/ping', payload, {force: true}).then(response => {

                }).catch(err => {
                    // Crossed a milestone threshold mid-session: flip the shared lock
                    // state so the paused banner and nav flag appear immediately
                    if (err.response?.status === 423) {
                        this.getMilestoneLock(this.enrollment.id);
                    }
                });
            },

            completeComponent: function() {
                if (this.milestoneLock.locked) {
                    // Guard against a stale button click (e.g. a milestone was crossed
                    // by a ping in another tab): don't even hit the API, just refresh
                    // the lock state so the banner/nav/button all catch up.
                    this.getMilestoneLock(this.enrollment.id);
                    return;
                }

                if (this.enrollment.course.show_timer && !this.enrollment.course.disable_timer && this.outline.require_min_time && Math.floor(this.time / 60 + this.outline.time <= this.outline.require_min_time)) {
                    this.$buefy.toast.open({
                        message: '<i class="fas fa-timer mr-2"></i>You have not completed the required time for this lesson.<br>Please review the content.',
                        type: 'is-danger',
                        duration: 7000,
                        position: 'is-bottom'
                    });
                } else {
                    let payload = {
                        component_id: this.component.id,
                        enrollment_id: this.enrollment.id,
                        type: 'course_outline',  //??
                        type_id: this.outline.id
                    };

                    ///

                    http.post('/app/enrollments/progress', payload, {force: true}).then(response => {
                        this.$emit('update');

                        // Completing the final lesson can complete the course
                        // (server-side predicate) — celebrate it right here
                        if (response.data && response.data.course_completed) {
                            console.log('Course completed — showing ceremony');
                            if (this.timer) clearInterval(this.timer);
                            this.ceremony = true;
                            // Refresh the enrollment so completed_at and the
                            // nav/application flags reflect the completion
                            this.getEnrollmentById(this.enrollmentId);
                            return;
                        }

                        // Or leave it in the student's hands: only optional
                        // applications remain, so the server withholds
                        // completion and we present the finish-or-wait choice
                        // (completing would lock study time they may still
                        // want, e.g. toward an optional app's minimum).
                        if (response.data && response.data.ready_to_complete) {
                            console.log('Coursework finished — completion awaiting student confirm');
                            if (this.timer) clearInterval(this.timer);
                            this.completionConfirm = response.data.completion_confirm || {};
                            this.readyToComplete = true;
                            return;
                        }

                        if (this.outline.nextSectionId) {
                            this.$router.push({name: 'overview', params: {enrollmentId: this.enrollmentId, sectionId: this.outline.nextSectionId}});
                        } else if (this.outline.nextOutlineId) {
                            this.$router.push({name: 'component', params: {enrollmentId: this.enrollmentId, outlineId: this.outline.nextOutlineId}});
                        } else {
                            console.log('Coursework complete');
                            this.$router.push({name: 'course', params: {enrollmentId: this.enrollmentId}});
                        }
                    }).catch(err => {
                        if (err.response?.status === 423) {
                            // Milestone lock kicked in between page load and this click
                            // (e.g. hours ticked over via a ping) — surface the real
                            // reason and refresh the shared lock state so the button
                            // and banner reflect it immediately.
                            this.getMilestoneLock(this.enrollment.id);
                            this.$buefy.dialog.alert({
                                title: 'Course Paused',
                                message: err.response.data.error || 'This course is paused pending approval of a milestone reflection.',
                                type: 'is-warning',
                                hasIcon: true,
                                icon: 'lock',
                                confirmText: 'OK'
                            });
                        } else {
                            window.alert('There was an issue completing the course content. If this problem persists please contact us.');
                        }
                    });
                }
            },
            goToOutline: function(id) {
                this.prevOutlineId = null;
                this.nextOutlineId = null;
                this.$router.push({name: 'component', params: {enrollmentId: this.enrollmentId, outlineId: id}});
            },
            finishCourse: function() {
                this.completing = true;
                http.post(`/app/enrollments/${this.enrollment.id}/complete`, {}, {force: true}).then(() => {
                    this.readyToComplete = false;
                    this.ceremony = true;
                    this.getEnrollmentById(this.enrollmentId);
                }).catch(err => {
                    const message = err.response?.data?.error || 'There was an issue completing your course. If this problem persists please contact us.';
                    this.$buefy.dialog.alert({
                        title: 'Unable to Complete',
                        message,
                        type: 'is-warning',
                        hasIcon: true,
                        confirmText: 'OK'
                    });
                }).finally(() => {
                    this.completing = false;
                });
            }
        },
        watch: {
            'currentItemId': function() {
                if (this.enrollment) {
                    this.loadByOutline();
                }
            },
            'enrollment': function(newVal, oldVal) {
                // Reload when enrollment loads or changes (handles page refresh)
                if (newVal && (!oldVal || newVal.id !== oldVal.id)) {
                    this.loadByOutline();
                }
            }
        },
        mounted: function() {
            // Only load if enrollment is already available, otherwise wait for watcher
            if (this.enrollment) {
                this.loadByOutline();
            }
        },
        destroyed: function() {
            if (this.timer) clearInterval(this.timer);
        },
        onIdle() {
            console.log('IDLE');
            this.timeout = 60; // start the grace countdown before auto-leaving
        },
        onActive() {
            console.log('ACTIVE');
            this.timeout = 60; // reset the grace countdown; do NOT ping (would back-count the idle gap)
        }
    };
</script>

<style lang="scss" scoped>
    .content-frame {
        position: absolute;
        top: 105px;
        left: 5rem;
        right: 4rem;
        bottom: 7rem;
        background-color: #fff;
        border-radius: 8px;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.3);
        padding: 2rem 2rem 10rem;

        overflow-x: hidden;
        overflow-y: scroll;
    }

    .time-paused-banner {
        position: absolute;
        top: 105px;
        left: 5rem;
        right: 4rem;
        z-index: 5;
        margin-bottom: 0;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.3);
    }

    .ceremony {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2rem;

        .ceremony-inner {
            max-width: 620px;
        }

        .ceremony-check {
            font-size: 6rem;
        }

        .ceremony-actions {
            max-width: 420px;
            margin: 0 auto;
        }
    }

</style>
